import { useQuery } from '@tanstack/react-query';
import { OperationsApi } from '@Apis';
import { EuqTimeRangeEnum } from '../../type';
import { useMemo } from 'react';

interface MitigationStatusCountsParams {
  orgId: string;
  timeRange: EuqTimeRangeEnum;
  enabled?: boolean;
}

interface MitigationStatusCounts {
  required: number;
  inProgress: number;
  completed: number;
}

export const useMitigationStatusCounts = ({
  orgId,
  timeRange,
  enabled = true,
}: MitigationStatusCountsParams): MitigationStatusCounts | undefined => {
  const { data: mitigationData, isLoading } = useQuery({
    queryKey: ['euqFeedbackStatistic', 'mitigation_status', timeRange, orgId],
    queryFn: () =>
      OperationsApi.apiGetEuqFeedbackStatistic({
        predefined: timeRange,
        dimension: 'mitigation_status',
      }),
    select: (data) => data.data,
    enabled: enabled && !!orgId,
    staleTime: 30000,
  });

  const counts = useMemo(() => {
    if (isLoading || !mitigationData) {
      return undefined;
    }

    // Helper function to calculate total from API data array
    const calculateTotal = (dataArray: any[]) => {
      if (!dataArray || !Array.isArray(dataArray)) return 0;
      return dataArray.reduce((sum, item) => sum + (item.count || 0), 0);
    };

    // Process mitigation status statistics
    // Status 4 = INITIALIZED (manual mitigation required)
    // Status 2 = IN_PROGRESS (auto mitigation in progress)
    // Status 1 = DONE (mitigation completed)
    const requiredCount = calculateTotal(mitigationData?.['4'] || []);
    const inProgressCount = calculateTotal(mitigationData?.['2'] || []);
    const completedCount = calculateTotal(mitigationData?.['1'] || []);

    return {
      required: requiredCount,
      inProgress: inProgressCount,
      completed: completedCount,
    };
  }, [mitigationData, isLoading]);

  return counts;
};
